import React from 'react';
import { Card, CardContent, Typography, Grid, Box } from '@mui/material';

interface TransactionSummaryCardProps {
    firstFieldName: string;
    walletNo: string;
    value: string | number;
    commissionValue: number;
    totalValue: number;
    currencyLabel?: string;
    formatMoney: (value: number) => string;
}

const TransactionSummaryCard: React.FC<TransactionSummaryCardProps> = ({
    firstFieldName,
    walletNo,
    value,
    commissionValue,
    totalValue,
    currencyLabel = '₺',
    formatMoney
}) => {
    const normalizeDisplayValue = (input: string | number): string => {
        if (typeof input === 'number') return input.toString();

        const currencyPattern = /(TL|TRY|USD|EUR|AU|XAU|₺|\$|€)/gi;
        return input
            .replace(new RegExp(`^\\s*${currencyPattern.source}\\s*`, 'i'), '')
            .replace(new RegExp(`\\s*${currencyPattern.source}\\s*$`, 'i'), '')
            .trim();
    };

    const displayValue = normalizeDisplayValue(value);
    const parseToNumber = (input: string | number): number | null => {
        if (typeof input === 'number') {
            return Number.isFinite(input) ? input : null;
        }

        const normalized = input
            .replace(/[^0-9.,-]/g, '')
            .replace(/\s/g, '');

        if (!normalized) return null;

        const lastComma = normalized.lastIndexOf(',');
        const lastDot = normalized.lastIndexOf('.');
        const decimalSeparator = lastComma > lastDot ? ',' : '.';

        let numericString = normalized;
        if (decimalSeparator === ',') {
            numericString = numericString.replace(/\./g, '').replace(',', '.');
        } else {
            numericString = numericString.replace(/,/g, '');
        }

        const parsed = Number(numericString);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const parsedValue = parseToNumber(value);
    const formatByCurrencyLabel = (amount: number): string => {
        const currencyCodeMap: Record<string, string> = {
            TL: 'TRY',
            TRY: 'TRY',
            '₺': 'TRY',
            USD: 'USD',
            '$': 'USD',
            EUR: 'EUR',
            '€': 'EUR'
        };

        const currencyCode = currencyCodeMap[currencyLabel.toUpperCase()];
        if (currencyCode) {
            return new Intl.NumberFormat('tr-TR', {
                style: 'currency',
                currency: currencyCode
            }).format(amount);
        }

        const numericFormatted = new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);

        return `${numericFormatted} ${currencyLabel}`;
    };

    return (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        textAlign: 'center',
        mt: 2
    }}>
        <Card
            elevation={0}
            sx={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                color: 'white',
                borderRadius: 3,
                p: { xs: 2, sm: 3 },
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{ pb: '16px !important', px: { xs: '16px !important', sm: '24px !important' } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    İşlem Özeti
                </Typography>
                <Grid container spacing={2} sx={{ textAlign: 'left' }}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            p: 2,
                            backdropFilter: 'blur(10px)',
                            overflow: 'hidden',
                            minHeight: '80px'
                        }}>
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                {firstFieldName}
                            </Typography>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 600,
                                    wordBreak: 'break-all',
                                    overflowWrap: 'break-word',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4
                                }}
                            >
                                {walletNo}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            p: 2,
                            backdropFilter: 'blur(10px)',
                            overflow: 'hidden',
                            minHeight: '80px'
                        }}>
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                Gönderilecek Tutar
                            </Typography>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 600,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4
                                }}
                            >
                                {parsedValue !== null ? formatByCurrencyLabel(parsedValue) : `${currencyLabel} ${displayValue}`}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            p: 2,
                            backdropFilter: 'blur(10px)',
                            overflow: 'hidden',
                            minHeight: '80px'
                        }}>
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                Komisyon Tutarı
                            </Typography>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 600,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4
                                }}
                            >
                                {formatByCurrencyLabel(commissionValue)}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: 2,
                            p: 2,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            overflow: 'hidden',
                            minHeight: '80px'
                        }}>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                Toplam Tutar
                            </Typography>
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    fontWeight: 700,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    fontSize: { xs: '1rem', sm: '1.25rem' },
                                    lineHeight: 1.4
                                }}
                            >
                                {formatByCurrencyLabel(totalValue)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    </Box>
    );
};

export default TransactionSummaryCard;
